import type { FunctionComponent } from 'react'
import type { ProductColour, ProductScoreGaugeProps } from '@which/seatbelt'
import { dynamicGa4DataLayerPush } from '@which/shared'

import type {
  AnnualRunningCost as AnnualRunningCostType,
  ProductOffer,
} from '../../../generated/frontend'
import type { CompareTray } from '../../types/ReviewsCompareTray'
import { usePageProps } from '../../usePageProps'
import { CardedDynamicTableLoggedOut } from './CardedDynamicTableLoggedOut'
import { CardedDynamicTableWrapper } from './CardedDynamicTableWrapper'

export const CardedDynamicTable: FunctionComponent<CardedDynamicTableProps> = ({
  dynamicTableId = '',
  logInCTA,
  products,
  signUpCTA,
  title,
  trackingPrefix = 'dynamic-table',
}) => {
  const {
    userAccessState: { transformTypeDecision },
  } = usePageProps()

  const isPaidMember = ['AUTHENTICATED_FULL_ACCESS', 'FULL_ACCESS'].includes(
    transformTypeDecision ?? ''
  )

  /*
    WHC-941-StackedCards requires splitting logged in and logged out before
    checking the feature flag so we only include logged out users in the experiment.
  */
  if (isPaidMember || trackingPrefix === 'dynamic-table') {
    return (
      <CardedDynamicTableWrapper
        dynamicTableId={dynamicTableId}
        logInCTA={logInCTA}
        products={products}
        signUpCTA={signUpCTA}
        title={title}
        trackingPrefix={trackingPrefix}
      />
    )
  }

  return (
    <CardedDynamicTableLoggedOut
      dynamicTableId={dynamicTableId}
      logInCTA={logInCTA}
      products={products}
      signUpCTA={signUpCTA}
      title={title}
      trackingPrefix={trackingPrefix}
    />
  )
}

export const runningCostTooltipHandler = (trackingPrefix: 'spp' | 'dynamic-table') => {
  dynamicGa4DataLayerPush({
    item_text: 'annual running cost',
    item_parent_text: 'product card',
    event: trackingPrefix === 'dynamic-table' ? 'clickTooltipDynamic' : 'clickTooltipSpp',
  })
}

export const formatSources = (image: CardedDynamicTableImage) => {
  const sources: { srcset: string; type: string }[] = []

  if (image.webp) {
    sources.push({ srcset: `${image.webp}`, type: 'image/webp' })
  }

  sources.push({ srcset: `${image.jpg}`, type: 'image/jpg' })

  return sources
}

export const getSources = (images: CardedDynamicTableImage[]) => {
  const frontImage = images.find((image) => image.view === 'front')

  if (frontImage) {
    return formatSources(frontImage)
  }

  return formatSources(images[0])
}

export const selectProductImage = (images: CardedDynamicTableImage[]) => {
  const frontImage = images.find((image) => image.view === 'front')
  return frontImage ? frontImage?.jpg : images[0].jpg
}

export type CardedDynamicTableImage = {
  jpg: string
  view: string
  webp: string | null
}

export type CardedDynamicTableProduct = {
  annualRunningCost?: AnnualRunningCostType
  availableOffersCount?: number
  badgeNames?: string[]
  businessKey?: string
  categorySlug?: string
  description?: string
  image?: {
    images: CardedDynamicTableImage[]
  }
  key?: string
  keyTestResults?: { label: string; tooltip: string }[]
  manufacturer?: {
    name: string
    slug: string
  }
  model?: string
  offers: ProductOffer[]
  overallScore?: number
  price?: string
  productScoreGauge?: ProductScoreGaugeProps
  slug?: string
  testDate?: string
  topRated: boolean
  variants?: ProductColour[][] | null
}

export type SharedCardedDynamicTableProps = {
  dynamicTableId?: string
  logInCTA?: { url: string; label: string; copy: string }
  signUpCTA?: { url: string; label: string; smallPrint?: string }
  trackingPrefix: 'spp' | 'dynamic-table'
}

export type CardedDynamicTableProps = SharedCardedDynamicTableProps & {
  products?: CardedDynamicTableProduct[]
  stackedCards?: boolean
  title?: string
}

export type TableItemProps = SharedCardedDynamicTableProps &
  CardedDynamicTableProduct & {
    addProductHandler: CompareTray['addProduct']
    handleSaveClick: (product: CardedDynamicTableProduct) => void
    index: number
    isAllowCompare?: boolean
    isSelectedForComparison: boolean
    isComparisonLimitReached: boolean
    loginStatus?: any
    removeProductHandler: CompareTray['removeProduct_ProductCardHandler']
    saved: boolean
    stackedCards?: boolean
  }
